import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { alertsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    AlertTriangle,
    RefreshCw,
    Bell,
    User,
    ChevronRight,
    Flame,
    TrendingDown,
    UserX,
    Check,
    Trash2
} from 'lucide-react';
import { getSeverityColor, formatDateTime } from '../../lib/utils';

const AlertCard = ({ alert, onDismiss, onDelete }) => {
    const getAlertIcon = (type) => {
        switch (type) {
            case 'burnout_risk':
                return Flame;
            case 'disengagement':
                return TrendingDown;
            case 'attrition_risk':
                return UserX;
            default:
                return AlertTriangle;
        }
    };

    const Icon = getAlertIcon(alert.alert_type);

    return (
        <Card className="bento-card-interactive">
            <CardContent className="p-4">
                <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        alert.severity === 'critical' ? 'bg-red-100' :
                        alert.severity === 'high' ? 'bg-orange-100' :
                        alert.severity === 'medium' ? 'bg-amber-100' :
                        'bg-emerald-100'
                    }`}>
                        <Icon className={`w-5 h-5 ${
                            alert.severity === 'critical' ? 'text-red-600' :
                            alert.severity === 'high' ? 'text-orange-600' :
                            alert.severity === 'medium' ? 'text-amber-600' :
                            'text-emerald-600'
                        }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-stone-900">{alert.employee_name}</h4>
                            <Badge className={`${getSeverityColor(alert.severity)} capitalize text-xs`}>
                                {alert.severity}
                            </Badge>
                            {alert.status === 'dismissed' && (
                                <Badge variant="outline" className="text-xs">Dismissed</Badge>
                            )}
                        </div>
                        <p className="text-sm text-stone-600 mb-2">{alert.description}</p>
                        <p className="text-xs text-stone-400">
                            {formatDateTime(alert.created_at)}
                        </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                        {alert.status !== 'dismissed' && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onDismiss(alert.id)}
                                className="text-stone-500 hover:text-stone-700"
                                data-testid={`dismiss-alert-${alert.id}`}
                            >
                                <Check className="w-4 h-4" />
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(alert.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-alert-${alert.id}`}
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {alert.recommended_actions?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-stone-100">
                        <p className="text-xs font-medium text-stone-500 uppercase mb-2">Recommended Actions</p>
                        <ul className="space-y-1">
                            {alert.recommended_actions.map((action, i) => (
                                <li key={i} className="text-sm text-stone-600 flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                                    {action}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default function AlertsPage() {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    const fetchAlerts = async () => {
        try {
            setLoading(true);
            const response = await alertsAPI.getAll();
            setAlerts(response.data || []);
        } catch (error) {
            console.error('Failed to fetch alerts:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();
    }, []);

    const handleGenerate = async () => {
        try {
            setGenerating(true);
            await alertsAPI.generate();
            await fetchAlerts();
        } catch (error) {
            console.error('Failed to generate alerts:', error);
        } finally {
            setGenerating(false);
        }
    };

    const handleDismiss = async (alertId) => {
        try {
            await alertsAPI.dismiss(alertId);
            await fetchAlerts();
        } catch (error) {
            console.error('Failed to dismiss alert:', error);
        }
    };

    const handleDelete = async (alertId) => {
        try {
            await alertsAPI.delete(alertId);
            await fetchAlerts();
        } catch (error) {
            console.error('Failed to delete alert:', error);
        }
    };

    const alertsByType = {
        all: alerts,
        burnout: alerts.filter(a => a.alert_type === 'burnout_risk'),
        disengagement: alerts.filter(a => a.alert_type === 'disengagement'),
        attrition: alerts.filter(a => a.alert_type === 'attrition_risk'),
    };

    const criticalCount = alerts.filter(a => a.severity === 'critical').length;
    const highCount = alerts.filter(a => a.severity === 'high').length;

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500">Loading alerts...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8" data-testid="alerts-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold font-heading text-stone-900 flex items-center gap-3">
                            <AlertTriangle className="w-8 h-8 text-coral-DEFAULT" />
                            Early Warning Alerts
                        </h1>
                        <p className="text-stone-500 mt-1">
                            Employees flagged for potential burnout, disengagement, or attrition risk.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <Button onClick={fetchAlerts} variant="outline" className="gap-2" data-testid="refresh-alerts-btn">
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                        </Button>
                        <Button
                            onClick={handleGenerate}
                            disabled={generating}
                            className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                            data-testid="generate-alerts-btn"
                        >
                            {generating ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Analyzing...
                                </>
                            ) : (
                                <>
                                    <Bell className="w-4 h-4" />
                                    Run Analysis
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <Card className="bento-card">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-stone-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-heading text-stone-900">{alerts.length}</p>
                                <p className="text-sm text-stone-500">Total Alerts</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bento-card border-red-200">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                                <Flame className="w-6 h-6 text-red-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-heading text-red-600">{criticalCount}</p>
                                <p className="text-sm text-stone-500">Critical</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bento-card border-orange-200">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                                <TrendingDown className="w-6 h-6 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-heading text-orange-600">{highCount}</p>
                                <p className="text-sm text-stone-500">High Priority</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bento-card border-amber-200">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                                <UserX className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-heading text-amber-600">
                                    {alertsByType.attrition.length}
                                </p>
                                <p className="text-sm text-stone-500">Attrition Risk</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Alerts List */}
                <Tabs defaultValue="all" className="space-y-6">
                    <TabsList className="bg-stone-100 p-1 rounded-xl">
                        <TabsTrigger value="all" className="rounded-lg">All ({alerts.length})</TabsTrigger>
                        <TabsTrigger value="burnout" className="rounded-lg">Burnout ({alertsByType.burnout.length})</TabsTrigger>
                        <TabsTrigger value="disengagement" className="rounded-lg">Disengagement ({alertsByType.disengagement.length})</TabsTrigger>
                        <TabsTrigger value="attrition" className="rounded-lg">Attrition ({alertsByType.attrition.length})</TabsTrigger>
                    </TabsList>

                    {Object.entries(alertsByType).map(([key, alertList]) => (
                        <TabsContent key={key} value={key}>
                            {alertList.length === 0 ? (
                                <Card className="bento-card">
                                    <CardContent className="p-12 text-center">
                                        <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
                                            <Bell className="w-8 h-8 text-stone-400" />
                                        </div>
                                        <h3 className="text-lg font-medium text-stone-900 mb-2">No alerts found</h3>
                                        <p className="text-stone-500 mb-4">
                                            Click "Run Analysis" to scan employee data for potential risks.
                                        </p>
                                    </CardContent>
                                </Card>
                            ) : (
                                <div className="space-y-4">
                                    {alertList.map((alert) => (
                                        <AlertCard 
                                            key={alert.id} 
                                            alert={alert} 
                                            onDismiss={handleDismiss}
                                            onDelete={handleDelete}
                                        />
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    ))}
                </Tabs>
            </motion.div>
        </div>
    );
}
